import { useState, useEffect } from 'react';
import { Smartphone, X, Download, Share } from 'lucide-react';

export default function PWAInstallPrompt() {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Expose functions to window for global access (like the landing page button)
        (window as any).triggerPWAInstall = handleInstall;

        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
            setIsInstalled(true);
            return;
        }

        // Detect Mobile Device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (!isMobile) return;

        // Detect iOS
        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        setIsIOS(ios);

        // Global listener for Chrome/Android/Edge
        const handler = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Only show if not dismissed in this session
            const dismissed = sessionStorage.getItem('pwa_prompt_dismissed');
            if (!dismissed) {
                setShowPrompt(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handler);

        // For iOS, show if not dismissed and not installed
        if (ios && !sessionStorage.getItem('pwa_prompt_dismissed')) {
            // Small delay to ensure page is loaded
            setTimeout(() => setShowPrompt(true), 2000);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            delete (window as any).triggerPWAInstall;
        };
    }, [deferredPrompt]);

    const handleInstall = async () => {
        if (!deferredPrompt && !isIOS) {
            // If on iOS or prompt not available, we show the manual instructions
            setShowPrompt(true);
            sessionStorage.removeItem('pwa_prompt_dismissed');
            return;
        }

        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setDeferredPrompt(null);
                setShowPrompt(false);
            }
        }
    };

    const dismissPrompt = () => {
        setShowPrompt(false);
        sessionStorage.setItem('pwa_prompt_dismissed', 'true');
    };

    if (isInstalled || !showPrompt) return null;

    return (
        <div className="fixed bottom-6 left-6 right-6 z-[100] animate-in slide-in-from-bottom-10 fade-in duration-500">
            <div className="max-w-md mx-auto bg-dark-card border border-primary/30 rounded-3xl shadow-2xl overflow-hidden shadow-primary/20">
                <div className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shrink-0">
                        <Smartphone size={24} />
                    </div>

                    <div className="flex-1">
                        <h3 className="text-text-main font-bold">Installer CitoyenPro</h3>
                        <p className="text-text-muted text-xs">Accédez à vos quiz instantanément depuis votre écran d'accueil.</p>
                    </div>

                    <button
                        onClick={dismissPrompt}
                        className="p-2 text-text-muted hover:text-text-main transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="px-5 pb-5">
                    {isIOS ? (
                        <div className="bg-dark-bg p-3 rounded-2xl border border-dark-border flex items-center gap-3">
                            <div className="bg-white/10 p-1.5 rounded-lg">
                                <Share size={16} className="text-blue-400" />
                            </div>
                            <p className="text-[11px] text-text-muted">
                                Appuyez sur <span className="text-text-main font-bold">"Partager"</span> puis <span className="text-text-main font-bold">"Sur l'écran d'accueil"</span>.
                            </p>
                        </div>
                    ) : (
                        <button
                            onClick={handleInstall}
                            className="w-full py-3 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                        >
                            <Download size={18} />
                            Installer maintenant
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
